import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { apiFile, fetchCaseOverrides } from "../lib/api";

const EASE = [0.16, 1, 0.3, 1];

export default function CaseStudy({ project, onClose, onNext }) {
  const [override, setOverride] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchCaseOverrides().then((all) => {
      if (alive) setOverride(all?.[project.id] || {});
    });
    return () => {
      alive = false;
    };
  }, [project.id]);

  const subtitle = override?.subtitle || project.subtitle;
  const description = override?.description || project.description;
  const services = override?.disciplines?.length ? override.disciplines : project.services;
  const posters = [
    ...(override?.extra_posters || []).map((p) => apiFile(p.storage_path)),
    ...project.posters,
  ];

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [project.id]);

  useEffect(() => {
    document.title = `${project.title} — Pix Marketing`;
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) {
      meta.setAttribute(
        "content",
        `${project.title} — ${subtitle}. Brand advertising and design by Pix Marketing.`
      );
    }
    return () => {
      document.title = "PIX MARKETING — Make Yourself Hard to Ignore.";
      if (meta && prev) meta.setAttribute("content", prev);
    };
  }, [project.id, subtitle]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.main
      key={project.id}
      data-testid="case-study-page"
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
      className="bg-ink pb-20"
    >
      <div className="sticky top-16 z-10 flex items-center justify-between border-b border-white/10 bg-ink/70 px-6 py-4 backdrop-blur-xl lg:top-20 lg:px-12">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 md:text-xs">
          Pix® — {project.title}
        </span>
        <button
          data-testid="case-study-close-btn"
          onClick={onClose}
          aria-label="Close case study"
          className="group flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-vermilion hover:bg-vermilion hover:text-ink active:bg-vermilion active:text-ink"
        >
          Close
          <X className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-90" />
        </button>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 pb-20 pt-8 lg:px-12 lg:pt-10">
        <span
          className="mb-5 inline-block rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em]"
          style={{ borderColor: project.accent, color: project.accent }}
        >
          {project.category}
        </span>

        <h2
          data-testid="case-study-title"
          className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight md:text-6xl"
        >
          {project.title}
        </h2>
        <p className="mt-2 text-sm text-neutral-400 md:text-lg">{subtitle}</p>

        <div className="mt-8 grid gap-8 md:mt-10 lg:grid-cols-12">
          <p className="text-sm leading-relaxed text-neutral-300 md:text-base lg:col-span-7">
            {description}
          </p>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">
              Disciplines
            </p>
            <ul>
              {services.map((s, i) => (
                <li
                  key={s}
                  className="flex items-center gap-3 border-t border-white/10 py-2.5 font-display text-sm font-bold uppercase tracking-tight text-white"
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: project.accent }}
                  />
                  <span className="mr-auto">{s}</span>
                  <span className="font-mono text-[10px] text-neutral-600">0{i + 1}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {project.landscape ? (
          <div
            data-testid="case-study-gallery"
            className="mx-auto mt-8 flex max-w-3xl flex-col gap-2 md:mt-10"
          >
            {posters.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: Math.min(0.2 + i * 0.05, 0.8) }}
                className="overflow-hidden rounded-md bg-panel shadow-[0_18px_40px_rgba(0,0,0,0.5)] ring-1 ring-white/10"
              >
                <img
                  src={src}
                  alt={`${project.title} brochure page ${i + 1}`}
                  loading="lazy"
                  className="w-full"
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div
            data-testid="case-study-gallery"
            className="mt-8 grid grid-cols-2 gap-3 md:mt-10 md:grid-cols-3 md:gap-4"
          >
            {posters.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE, delay: 0.2 + i * 0.07 }}
                className="aspect-[2/3] overflow-hidden rounded-2xl bg-panel"
              >
                <img
                  src={src}
                  alt={`${project.title} poster ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
                  style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
                />
              </motion.div>
            ))}
          </div>
        )}

        <button
          data-testid="case-study-next-btn"
          onClick={onNext}
          className="group mt-14 flex w-full items-center justify-between border-t border-white/10 pt-8 text-left"
        >
          <div>
            <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              Next Case
            </p>
            <span
              className="font-display text-3xl font-extrabold uppercase tracking-tight transition-colors duration-300 md:text-6xl"
              onMouseEnter={(e) => (e.currentTarget.style.color = project.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "")}
            >
              Keep Exploring
            </span>
          </div>
          <span
            className="flex h-14 w-14 items-center justify-center rounded-full border border-white/20 transition-all duration-300 group-hover:border-transparent md:h-20 md:w-20"
            onMouseEnter={(e) => (e.currentTarget.style.background = project.accent)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <ArrowRight className="h-5 w-5 text-white transition-colors duration-300 group-hover:text-ink md:h-8 md:w-8" />
          </span>
        </button>
      </div>
    </motion.main>
  );
}